package com.wintermar.inspection.core.ui.component.button

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.wintermar.inspection.core.ui.component.button.variant.ButtonVariant
import com.wintermar.inspection.core.ui.component.button.variant.toModel
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.leading_icon_button
import inspection.composeapp.generated.resources.trailing_icon_button
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/**
 * A custom composable button that provides various states and styles based on the application's theme.
 * It supports different variants, loading and disabled states, and optional leading/trailing icons.
 *
 * @param buttonVariant The variant of the button, which determines its appearance (e.g., SmallPrimary, MediumSecondary).
 * @param text The text to display on the button.
 * @param modifier The [Modifier] to be applied to the button.
 * @param hideTextWhenLoading If true, the text will be hidden when the button is in a loading state. Defaults to false.
 * @param disabled Controls the enabled state of the button. When `true`, this button will not be clickable.
 * @param isLoading If true, the button will display a loading indicator. The button will also be disabled.
 * @param trailingIcon An optional drawable resource to be displayed after the text.
 * @param leadingIcon An optional drawable resource to be displayed before the text.
 * @param onClick The lambda to be executed when the button is clicked.
 */
@Composable
fun Button(
    buttonVariant: ButtonVariant,
    text: String,
    modifier: Modifier = Modifier,
    mandatory: Boolean = false,
    hideTextWhenLoading: Boolean = false,
    disabled: Boolean = false,
    isLoading: Boolean = false,
    trailingIcon: DrawableResource? = null,
    leadingIcon: DrawableResource? = null,
    onClick: () -> Unit,
) {
    val variant = buttonVariant.toModel()
    val dimension = LocalAppTheme.current.dimension

    Button(
        enabled = !isLoading && !disabled,
        shape = RoundedCornerShape(dimension.radius.md),
        colors = ButtonDefaults.buttonColors(
            contentColor = variant.stateColor.default.textColor,
            containerColor = variant.stateColor.default.backgroundColor,
            disabledContainerColor = variant.stateColor.disabled.backgroundColor,
            disabledContentColor = variant.stateColor.disabled.textColor,
        ),
        border = if (variant.stateColor.default.borderColor == Color.Unspecified) null else BorderStroke(
            width = 1.dp,
            color = if (!isLoading && !disabled) variant.stateColor.default.borderColor
            else variant.stateColor.disabled.borderColor
        ),
        modifier = Modifier.height(variant.size.height).then(modifier),
        contentPadding = PaddingValues(
            vertical = variant.size.verticalPadding, horizontal = variant.size.horizontalPadding
        ),
        onClick = { onClick.invoke() }) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(
                space = LocalAppTheme.current.dimension.spacing.space2,
                alignment = Alignment.CenterHorizontally
            ),
        ) {
            if (!isLoading && leadingIcon != null) {
                Icon(
                    painter = painterResource(leadingIcon),
                    modifier = Modifier.size(dimension.size.icon.s),
                    contentDescription = stringResource(Res.string.leading_icon_button)
                )
            }

            if (!(hideTextWhenLoading && isLoading)) {
                Row {
                    Text(
                        text = text,
                        style = LocalAppTheme.current.textStyle.body.small,
                        fontWeight = FontWeight.Bold
                    )
                    if (mandatory) {
                        Text(
                            text = "*",
                            color = LocalAppTheme.current.color.text.error.default,
                            style = LocalAppTheme.current.textStyle.body.small,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }

            if (!isLoading && trailingIcon != null) {
                Icon(
                    painter = painterResource(trailingIcon),
                    modifier = Modifier.size(dimension.size.icon.s),
                    contentDescription = stringResource(Res.string.trailing_icon_button)
                )
            }

            if (isLoading) {
                CircularProgressIndicator(
                    modifier = Modifier.size(dimension.size.icon.s / 1.5f),
                    strokeWidth = 2.dp,
                    color = LocalAppTheme.current.color.icon.disabled
                )
            }
        }
    }
}
